import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import {
  BOOKING_DRAWER_ACCENT,
  BOOKING_DRAWER_WIDTH_SX,
  getBookingDrawerFooterSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPaperSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerTheme,
} from '../../utils/bookingDrawerLayout';
import { getDrawerRootSx } from '../../utils/drawerOverlayProps';

const normalizeInvoiceUrl = (url) => {
  const value = String(url || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return value.startsWith('/') ? value : `/${value}`;
};

const formatAmount = (amount, currency = 'INR') => {
  const numeric = Number(amount || 0);
  const code = String(currency || 'INR').toUpperCase();
  return `${code} ${numeric.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const formatBillingLabel = (billing) => {
  const key = String(billing || '').trim().toLowerCase();
  if (key === 'annual' || key === 'yearly') return 'Yearly';
  if (key === 'quarterly') return 'Quarterly';
  if (key === 'monthly') return 'Monthly';
  return billing || '—';
};

export default function SubscriptionBillingDrawer({ open, onClose, hotel }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const drawerTheme = getBookingDrawerTheme(isDark);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoices, setInvoices] = useState([]);

  const hotelId = hotel?.id;
  const hotelName = hotel?.name || (hotelId ? `Hotel #${hotelId}` : 'Hotel');

  const loadInvoices = useCallback(async () => {
    if (!hotelId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/onboarding/api/subscription/invoices/', {
        params: { hotelId },
        withCredentials: true,
      });
      setInvoices(Array.isArray(data?.invoices) ? data.invoices : []);
    } catch (err) {
      setInvoices([]);
      setError(
        err?.response?.data?.error
        || err?.response?.data?.detail
        || 'Unable to load invoices for this hotel.'
      );
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  useEffect(() => {
    if (!open || !hotelId) return undefined;
    loadInvoices();
    return undefined;
  }, [open, hotelId, loadInvoices]);

  const openInvoice = (url) => {
    const normalized = normalizeInvoiceUrl(url);
    if (!normalized) return;
    window.open(normalized, '_blank', 'noopener,noreferrer');
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={getDrawerRootSx()}
      PaperProps={{
        sx: {
          ...getBookingDrawerPaperSx(isDark),
          width: BOOKING_DRAWER_WIDTH_SX,
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            ...getBookingDrawerHeaderSx(isDark),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <ReceiptLongIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 24 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600, lineHeight: 1.2 }}>
                Invoices & Billing
              </Typography>
              <Typography
                noWrap
                sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: 12, fontWeight: 600 }}
              >
                {hotelName}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}
            aria-label="Close billing drawer"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {loading ? (
            <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={32} sx={{ color: BOOKING_DRAWER_ACCENT }} />
            </Box>
          ) : null}

          {!loading && error ? (
            <Box
              sx={{
                p: 2,
                borderRadius: '12px',
                border: `1px solid ${alpha('#ff4861', 0.35)}`,
                bgcolor: alpha('#ff4861', isDark ? 0.12 : 0.06),
              }}
            >
              <Typography sx={{ color: '#ff4861', fontWeight: 700, fontSize: 13 }}>{error}</Typography>
            </Box>
          ) : null}

          {!loading && !error && invoices.length === 0 ? (
            <Box
              sx={{
                py: 6,
                px: 2,
                textAlign: 'center',
                borderRadius: '12px',
                border: `1px dashed ${isDark ? drawerTheme.surfaceBorder : '#d0d7de'}`,
              }}
            >
              <Typography sx={{ color: isDark ? drawerTheme.text : '#334155', fontWeight: 700 }}>
                No invoices yet
              </Typography>
              <Typography sx={{ color: isDark ? drawerTheme.subtleText : '#64748b', fontSize: 13, mt: 0.75 }}>
                Paid subscription and add-on invoices for this hotel will appear here.
              </Typography>
            </Box>
          ) : null}

          {!loading && !error ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {invoices.map((invoice) => {
                const addonLabels = Array.isArray(invoice.addons) ? invoice.addons : [];
                return (
                  <Box
                    key={invoice.id || invoice.order_code}
                    sx={{
                      p: 1.5,
                      borderRadius: '12px',
                      border: `1px solid ${isDark ? alpha(BOOKING_DRAWER_ACCENT, 0.2) : alpha(BOOKING_DRAWER_ACCENT, 0.16)}`,
                      bgcolor: isDark ? alpha('#0f172a', 0.35) : '#ffffff',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.6 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, color: isDark ? drawerTheme.text : '#0f172a', fontSize: 14 }}>
                          {invoice.package_name || invoice.package_id || 'Plan'}
                        </Typography>
                        <Typography sx={{ color: isDark ? drawerTheme.subtleText : '#64748b', fontSize: 12 }}>
                          {invoice.order_code || '—'} · {formatDateTime(invoice.paid_at || invoice.created_at)}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={invoice.kind || 'Subscription'}
                        sx={{
                          height: 24,
                          fontWeight: 800,
                          bgcolor: invoice.addon_only
                            ? alpha('#f59e0b', isDark ? 0.18 : 0.12)
                            : alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.18 : 0.12),
                          color: invoice.addon_only ? '#f59e0b' : BOOKING_DRAWER_ACCENT,
                          border: `1px solid ${alpha(invoice.addon_only ? '#f59e0b' : BOOKING_DRAWER_ACCENT, 0.35)}`,
                        }}
                      />
                    </Box>

                    <Typography sx={{ color: isDark ? drawerTheme.subtleText : '#475569', fontSize: 12, mb: 1 }}>
                      {formatBillingLabel(invoice.billing)}
                      {Number(invoice.rooms || 0) > 0 ? ` · ${invoice.rooms} rooms` : ''}
                      {addonLabels.length ? ` · Add-ons: ${addonLabels.join(', ')}` : ''}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ color: BOOKING_DRAWER_ACCENT, fontWeight: 900, fontSize: 16 }}>
                        {formatAmount(invoice.amount_major, invoice.currency)}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
                          onClick={() => openInvoice(invoice.invoice_preview_url || invoice.invoice_url)}
                          disabled={!invoice.invoice_preview_url && !invoice.invoice_url}
                          sx={{
                            ...getBookingDrawerOutlinedButtonSx(isDark),
                            textTransform: 'none',
                            fontWeight: 800,
                            borderRadius: 999,
                            minHeight: 30,
                            px: 1.3,
                          }}
                        >
                          Preview
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                          onClick={() => openInvoice(invoice.invoice_url)}
                          disabled={!invoice.invoice_url}
                          sx={{
                            ...getBookingDrawerPrimaryButtonSx(isDark),
                            textTransform: 'none',
                            fontWeight: 800,
                            borderRadius: 999,
                            minHeight: 30,
                            px: 1.3,
                          }}
                        >
                          Download
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : null}
        </Box>

        <Box sx={{ p: 1.5, display: 'flex', gap: 1.5, ...getBookingDrawerFooterSx(isDark) }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={onClose}
            sx={{
              ...getBookingDrawerOutlinedButtonSx(isDark),
              textTransform: 'none',
              fontWeight: 800,
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            fullWidth
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
            onClick={loadInvoices}
            disabled={loading || !hotelId}
            sx={{
              ...getBookingDrawerPrimaryButtonSx(isDark),
              textTransform: 'none',
              fontWeight: 800,
            }}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
